import React, { Fragment } from 'react'
import { Link } from 'gatsby'
import { useMediaLayout } from 'use-media'
import classnames from 'classnames'
import { Columns, Column } from 'react-flex-columns'
import formatDate from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import Icon from 'shared/ui/Icon'
import team from '../data/team'
import { TagList } from '../ui/Tags'
import MdxContent from '../ui/MdxContent'
import Avatar from '../ui/Avatar'
import BlogClaps from '../ui/blog/BlogClaps'
import BlogWorkshopMessage from '../ui/blog/BlogWorkshopMessage'
import './BlogArticlePage.scss'

function BlogArticlePage({ children, pageContext, location }) {
  const largeView = useMediaLayout({ minWidth: '800px' })

  const {
    title,
    image,
    author,
    date,
    imageAttribution,
    tallImage,
    tags,
    titleSize,
    tweet,
  } = pageContext.frontmatter

  // The slug is not provided to us by gatsby-mdx so we have to get it from
  // the location
  const slug = location.pathname
    .replace(/^\/|\/$/g, '')
    .split('/')
    .slice(-1)[0]

  const { name, twitterHandle, avatarUrl } = team.find((t) => t.id === author)
  const dateObj = parseISO(date)

  return (
    <div className="blog-layout">
      {image && (
        <div
          className={classnames('blog-hero-image', { 'blog-hero-image-tall': tallImage })}
          style={{ backgroundImage: `url(${image})` }}
        >
          <span className="blog-hero-image-attribution">{imageAttribution}</span>
        </div>
      )}
      {/* Microformat class names being used */}
      <article className="blog-body h-entry">
        <header className="blog-header spacing-small">
          <Columns gutterSize={0.5} middle>
            <Column>
              <div className="avatar-wrap">
                <Avatar src={avatarUrl} size={2} />
              </div>
            </Column>
            <Column flex className="text-small spacing-xsmall">
              <div className="p-author">{name}</div>
              <a href={`https://twitter.com/${twitterHandle}`} className=" block icon-text">
                <Icon name="twitter" />
                <span>{twitterHandle}</span>
              </a>
            </Column>
            <Column
              className={classnames('align-right', {
                spacing: largeView,
              })}
            >
              <div>
                <time
                  className="dt-published text-small"
                  dateTime={`${formatDate(dateObj, 'yyyy-MM-dd')} 12:00:00`}
                >
                  {formatDate(dateObj, 'MMM do, yyyy')}
                </time>
              </div>
              <div>
                <Link
                  to="/public-workshops"
                  className={classnames({
                    'button button-small icon-text': largeView,
                  })}
                >
                  {largeView && <Icon name="arrow-right" size={2} color="lightBlue" />}
                  {largeView ? (
                    <span>Attend a Public Workshop</span>
                  ) : (
                    <span>Public Workshops</span>
                  )}
                </Link>
              </div>
            </Column>
          </Columns>
          <hr />
          <div className="blog-title">
            <h1 style={{ fontSize: `${titleSize || 3}em` }} className="p-name">
              {title}
            </h1>
          </div>
          <hr />
          {Array.isArray(tags) && (
            <div className="align-center">
              <span className="text-small">Tags:</span> <TagList tags={tags} />
            </div>
          )}
        </header>
        <div className="blog-content e-content">
          <MdxContent children={children} slug={slug} pageContext={pageContext} />
        </div>
        <BlogWorkshopMessage />
        <footer className="blog-footer">
          <Columns split>
            <Column className="spacing">
              <BlogClaps slug={slug} />
            </Column>
            <Column className="horizontal-spacing">
              <Link to="/newsletter" className="icon-text">
                <Icon name="react" />
                <span>Newsletter</span>
              </Link>
              <a href="/rss.xml" className="icon-text">
                <Icon name="rss" />
                <span>RSS</span>
              </a>
            </Column>
          </Columns>
          {tweet && (
            <Fragment>
              <p>
                While we don't have blog comments, we tweeted about this posting when it went live
                so we welcome your comments there:
              </p>
              <div>
                <a className="button icon-text" href={tweet}>
                  <Icon name="twitter" />
                  <span>Comments</span>
                </a>
              </div>
            </Fragment>
          )}
        </footer>
      </article>
    </div>
  )
}

export default BlogArticlePage
